import { Timestamp } from "./Global";
import { User } from "../User";
import { CouponStatus } from "./Coupon";

export enum TransactionStatus {
  Successful = "successful",
  Failed = "failed",
  Completed = "completed",
}

export enum BalanceTransactionType {
  Add = "balance_add",
  Remove = "balance_remove",
  Set = "balance_set",
  BetPlaced = "bet_placed",
  BetWon = "bet_won",
  BetRefunded = "bet_refunded",
  // A cash out: the amount credited to the player, like a win (signed +).
  BetCashedOut = "bet_cashed_out",
  BetCreditSpent = "bet_credit_spent",
  BetCreditRefunded = "bet_credit_refunded",
  // Casino (real-mode game wallet) ledger rows, side by side with the bet_*
  // family: game_stake ↔ bet_placed, game_payout ↔ bet_won, game_refund ↔
  // bet_refunded, game_payout_reversal ↔ bet_won_reversal (reserved, no
  // writer yet), game_credit_* ↔ bet_credit_*. The *_funded members are the
  // sub-dealer's mirror side of a player stake/payout/refund.
  GameStake = "game_stake",
  GamePayout = "game_payout",
  GameRefund = "game_refund",
  GamePayoutReversal = "game_payout_reversal",
  GameStakeFunded = "game_stake_funded",
  GamePayoutFunded = "game_payout_funded",
  GameRefundFunded = "game_refund_funded",
  GameCreditSpent = "game_credit_spent",
  GameCreditRefunded = "game_credit_refunded",
  // Dealer exchange (DEALER-EXCHANGE-PLAN §4, §14b D2). Money rows on the
  // player (ref_type "exchange", ref = market id): a reserve holds the
  // player's worst case in escrow, a release returns unused escrow, a payout
  // credits a settlement and an adjustment is a signed resettlement
  // correction. Credit rows on the MAIN dealer (ref_type "exchange_order",
  // ref = order id): spent at an order's first fill, refunded on a void, the
  // refund reversed by a resettlement. Amounts are stored signed.
  ExchangeReserve = "exchange_reserve",
  ExchangeRelease = "exchange_release",
  ExchangePayout = "exchange_payout",
  ExchangeAdjustment = "exchange_adjustment",
  ExchangeCreditSpent = "exchange_credit_spent",
  ExchangeCreditRefunded = "exchange_credit_refunded",
  ExchangeCreditRefundedReversal = "exchange_credit_refunded_reversal",
}

export enum TransactionRefType {
  Coupon = "coupon",
  User = "user",
  // Dealer exchange money rows: ref is the exchange market id.
  Exchange = "exchange",
  // Dealer exchange credit rows: ref is the exchange order id.
  ExchangeOrder = "exchange_order",
}

export interface BalanceTransaction extends Timestamp {
  id: number;
  type: BalanceTransactionType;
  previousBalance: number;
  newBalance: number;
  currentBalance: number;
  status: TransactionStatus;
  amount: number;
  // Which balance this movement touched: "credit" (Admin/Agent/Dealer) vs
  // "money" (SubDealer/User), from the transaction metadata. Null for legacy
  // rows. Credit-only screens (dealer credits) filter on this.
  balanceType: "credit" | "money" | null;
  createdAt: string;
  updatedAt: string;
  // Username of whoever performed the movement, empty when the ledger names
  // nobody. Always read together with `addedByKind`, which says WHY it is
  // empty — an empty string alone must never be rendered as a name.
  addedBy: string;
  addedByKind: TransactionActorKind;
  // Status of the coupon this row references ("coupon" refType only, null
  // otherwise). A manual cancellation and a system void share one ledger type
  // (bet_refunded); the coupon's status is what tells them apart.
  couponStatus: CouponStatus | null;
  userName: string;
  user: User;
  ref: string;
  refType: TransactionRefType;
  notes: string | null;
}

/**
 * Who stands behind a ledger row.
 * - `user`    — a person did it; `addedBy` holds their username.
 * - `system`  — no person: the row was written by settlement/procedures.
 * - `unknown` — a person did it but the ledger does not record which account
 *               (refund rows written before sinek-api migration 116).
 */
export type TransactionActorKind = "user" | "system" | "unknown";
